import { BASE_URL } from './config';

const apiRequest = async ({
  endpoint,
  method = 'GET',
  token,
  params = {},
  body = null,
  headers = {},
}) => {
  try {
    const urlParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        urlParams.append(key, value);
      }
    });

    const url = `${BASE_URL}${endpoint}${
      urlParams.toString() ? '?' + urlParams.toString() : ''
    }`;

    const defaultHeaders = {
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : undefined,
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      Expires: '0',
      ...headers,
    };

    Object.keys(defaultHeaders).forEach(key => {
      if (defaultHeaders[key] === undefined) {
        delete defaultHeaders[key];
      }
    });

    const fetchOptions = {
      method,
      headers: defaultHeaders,
    };

    if (body) {
      if (body instanceof FormData) {
        fetchOptions.body = body;
        delete fetchOptions.headers['Content-Type'];
      } else {
        fetchOptions.body = JSON.stringify(body);
      }
    }

    const res = await fetch(url, fetchOptions);

    if (res.status === 304) {
      console.log(`${endpoint} not modified (304)`);
      return { success: true, data: null, notModified: true };
    }

    const data = await res.json();

    if (res.ok) {
      return { success: true, data };
    } else {
      return {
        success: false,
        error:
          data.message || data.error || `HTTP error! status: ${res.status}`,
        data,
        httpStatus: res.status,
      };
    }
  } catch (error) {
    console.error(`API Request Error (${endpoint}):`, error);
    return { success: false, error: error.message };
  }
};

export const getAdminOverview = async ({ token, from, to } = {}) => {
  return apiRequest({
    endpoint: 'admin/dashboard/overview',
    method: 'GET',
    token,
    params: { from, to },
  });
};

export const getSatuanDataProduct = async token => {
  return apiRequest({
    endpoint: 'admin/stamp-price',
    method: 'GET',
    token,
  });
};

export const editSatuanDataProduct = async (formData, token) => {
  return apiRequest({
    endpoint: 'admin/stamp-price',
    method: 'POST',
    token,
    body: formData,
  });
};

export const getTrendDetails = async ({ token, from, to } = {}) => {
  return apiRequest({
    endpoint: 'admin/dashboard/trend',
    method: 'GET',
    token,
    params: { from, to },
  });
};

export const getProductData = async (params = {}, token) => {
  return apiRequest({
    endpoint: 'admin/bundles',
    method: 'GET',
    token,
    params: {
      search: params.search,
      active: params.active,
      page: params.page,
      per: params.per_page,
    },
  });
};

export const createProductBundleAdmin = async (formData, token) => {
  return apiRequest({
    endpoint: 'admin/bundles',
    method: 'POST',
    token,
    body: formData,
  });
};

export const editProductBundleAdmin = async (bundleId, formData, token) => {
  return apiRequest({
    endpoint: `admin/bundles/${bundleId}`,
    method: 'PUT',
    token,
    body: formData,
  });
};

export const deleteProductBundleAdmin = async (bundleId, token) => {
  return apiRequest({
    endpoint: `admin/bundles/${bundleId}`,
    method: 'DELETE',
    token,
  });
};

export const getProductChart = async (params = {}, token) => {
  return apiRequest({
    endpoint: 'admin/stats/orders-monthly',
    method: 'GET',
    token,
    params,
  });
};

export const getVoucherData = async (params = {}, token) => {
  return apiRequest({
    endpoint: 'admin/vouchers',
    method: 'GET',
    token,
    params: {
      search: params.search,
      active: params.active,
      page: params.page,
      per_page: params.per_page,
    },
  });
};

export const createVoucherAdmin = async (formData, token) => {
  return apiRequest({
    endpoint: 'admin/vouchers',
    method: 'POST',
    token,
    body: formData,
  });
};

export const editVoucherAdmin = async (voucherId, formData, token) => {
  return apiRequest({
    endpoint: `admin/vouchers/${voucherId}`,
    method: 'PUT',
    token,
    body: formData,
  });
};

export const deleteVoucherAdmin = async (voucherId, token) => {
  return apiRequest({
    endpoint: `admin/vouchers/${voucherId}`,
    method: 'DELETE',
    token,
  });
};

export const getStatusDistribution = async ({ token, from, to } = {}) => {
  return apiRequest({
    endpoint: 'admin/dashboard/status-distribution',
    method: 'GET',
    token,
    params: { from, to },
  });
};

export const getDocumentsAdmin = async ({
  token,
  from,
  to,
  search,
  status,
  page,
} = {}) => {
  return apiRequest({
    endpoint: 'admin/documents',
    method: 'GET',
    token,
    params: { from, to, search, status, page },
  });
};

export const getAdminTransactions = async ({
  token,
  from,
  to,
  search,
  page,
} = {}) => {
  return apiRequest({
    endpoint: 'admin/transactions',
    method: 'GET',
    token,
    params: { from, to, search, page },
  });
};

export const getAdminUsers = async ({
  token,
  from,
  to,
  page,
  per_page,
  search,
} = {}) => {
  return apiRequest({
    endpoint: 'admin/users',
    method: 'GET',
    token,
    params: { from, to, search, page, per_page },
  });
};

export const deleteDocument = async (docId, token) => {
  return apiRequest({
    endpoint: `documents/${docId}`,
    method: 'DELETE',
    token,
  });
};

export const notifAdmin = async (params = {}, token) => {
  return apiRequest({
    endpoint: '/admin/notifications',
    method: 'GET',
    token,
    params: { user_after: params.user_after, tx_after: params.tx_after },
  });
};

export const getReportedUser = async (params = {}, token) => {
  return apiRequest({
    endpoint: 'laporan/laporan',
    method: 'GET',
    token,
    params: {
      from: params.from,
      to: params.to,
      page: params.page,
      limit: params.limit,
    },
  });
};

export const getUserAttachmentReport = async (params = {}, token) => {
  try {
    const urlParams = new URLSearchParams();
    if (params.ticketId) urlParams.append('ticketId', params.ticketId);
    if (params.fileKey) urlParams.append('fileKey', params.fileKey);
    const url = `${BASE_URL}laporan/attachments/?${urlParams.toString()}`;
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    });
    if (res.ok) {
      const blob = await res.blob();
      return { success: true, data: blob };
    } else {
      return { success: false, error: `HTTP error! status: ${res.status}` };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const createReplyLaporan = async (ticketId, formData, token) => {
  return apiRequest({
    endpoint: `laporan/laporan/${ticketId}/reply`,
    method: 'POST',
    token,
    body: formData,
  });
};

export const getReportTicketChart = async (params = {}, token) => {
  return apiRequest({
    endpoint: 'laporan/laporan/charts/monthly',
    method: 'GET',
    token,
    params: { from: params.from, to: params.to },
  });
};

export const getReportCategoryChart = async (params = {}, token) => {
  return apiRequest({
    endpoint: 'laporan/laporan/charts/category-distribution',
    method: 'GET',
    token,
    params: { from: params.from, to: params.to },
  });
};

export const exportDashboardData = async (params = {}, token) => {
  try {
    const urlParams = new URLSearchParams();
    if (params.from) urlParams.append('from', params.from);
    if (params.to) urlParams.append('to', params.to);
    const url = `${BASE_URL}admin/export/overview.pdf?${urlParams.toString()}`;
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
        Expires: '0',
      },
    });
    if (res.ok) {
      const blob = await res.blob();
      return { success: true, data: blob };
    } else {
      return { success: false, error: `HTTP error! status: ${res.status}` };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const exportTransactionData = async (params = {}, token) => {
  try {
    const urlParams = new URLSearchParams();
    if (params.from) urlParams.append('from', params.from);
    if (params.to) urlParams.append('to', params.to);
    if (params.search) urlParams.append('search', params.search);
    const url = `${BASE_URL}admin/export/transactions.pdf?${urlParams.toString()}`;
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
        Expires: '0',
      },
    });
    if (res.ok) {
      const blob = await res.blob();
      return { success: true, data: blob };
    } else {
      return { success: false, error: `HTTP error! status: ${res.status}` };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const exportDocumentData = async (params = {}, token) => {
  try {
    const urlParams = new URLSearchParams();
    if (params.from) urlParams.append('from', params.from);
    if (params.to) urlParams.append('to', params.to);
    if (params.search) urlParams.append('search', params.search);
    if (params.status) urlParams.append('status', params.status);
    if (params.per_page) urlParams.append('per_page', params.per_page);
    const url = `${BASE_URL}admin/export/documents.pdf?${urlParams.toString()}`;
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
        Expires: '0',
      },
    });
    if (res.ok) {
      const blob = await res.blob();
      return { success: true, data: blob };
    } else {
      return { success: false, error: `HTTP error! status: ${res.status}` };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const exportUserData = async (params = {}, token) => {
  try {
    const urlParams = new URLSearchParams();
    if (params.from) urlParams.append('from', params.from);
    if (params.to) urlParams.append('to', params.to);
    if (params.search) urlParams.append('search', params.search);
    const url = `${BASE_URL}admin/export/users.pdf?${urlParams.toString()}`;
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
        Expires: '0',
      },
    });
    if (res.ok) {
      const blob = await res.blob();
      return { success: true, data: blob };
    } else {
      return { success: false, error: `HTTP error! status: ${res.status}` };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const exportUserReportData = async (params = {}, token) => {
  try {
    const urlParams = new URLSearchParams();
    if (params.from) urlParams.append('from', params.from);
    if (params.to) urlParams.append('to', params.to);
    const url = `${BASE_URL}admin/export/laporan-aduan.pdf?${urlParams.toString()}`;
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
        Expires: '0',
      },
    });
    if (res.ok) {
      const blob = await res.blob();
      return { success: true, data: blob };
    } else {
      return { success: false, error: `HTTP error! status: ${res.status}` };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};
